/**
 * @fileoverview Global time filter context for consistent filtering across all pages.
 * @module contexts/TimeFilterContext
 *
 * Single source of truth: MainApp calls `useTimeFilter()` once and passes
 * the result straight through this provider's `value` prop. We intentionally
 * do NOT call the hook inside the provider — doing so would create a second,
 * divergent copy of the selection state.
 *
 * CANONICAL TIME AXIS: { startDate, endDate } via `dateRange`.
 * DERIVED: `years` (lossy for sub-year periods — 1m/3m/6m all become 1).
 *
 * Use dateRange for date-capable endpoints (transactions, Sankey).
 * Use years for matview endpoints (snapshot, heatmap, trajectory).
 *
 * INVARIANT: Every agency-specific, time-relevant view MUST read from this
 * context — never hand-pass timePeriod as props. Central injection ensures
 * consistent filtering across all procurement/vendor views.
 */

import React, { createContext, useContext } from 'react';

/**
 * Shape mirrors the return value of `useTimeFilter()`:
 *   { timePeriod, setTimePeriod, years, legacyPeriod, dateRange }
 */
const TimeFilterContext = createContext(null);

/**
 * Passthrough provider — `value` is the object returned by `useTimeFilter()`.
 * @param {{ value: object, children: React.ReactNode }} props
 */
export function TimeFilterProvider({ value, children }) {
  return <TimeFilterContext.Provider value={value}>{children}</TimeFilterContext.Provider>;
}

/**
 * Read the global time filter state from anywhere under <TimeFilterProvider>.
 * Returns a safe no-op shape when used outside a provider so pages that render
 * in isolation (tests, storybook) don't crash.
 * @returns {object} time filter state
 */
export function useTimeFilterContext() {
  const ctx = useContext(TimeFilterContext);
  if (!ctx) {
    // Safe no-op fallback for isolated pages (default: last 3 years)
    return {
      timePeriod: '3y',
      setTimePeriod: () => {},
      years: 3,
      legacyPeriod: 'last-3-years',
      dateRange: { startDate: null, endDate: null }, // Fallback: all-time (safe default)
    };
  }
  return ctx;
}

export default TimeFilterContext;
